import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { appSettings } from '../helpers/settings';
import { uniqueNamesGenerator, starWars } from 'unique-names-generator';
import { ToastContainer } from 'react-toastify';
import { EthereumClient, w3mConnectors, w3mProvider } from '@web3modal/ethereum';
import { Web3Modal } from '@web3modal/react';
import { configureChains, createClient, WagmiConfig, useNetwork } from 'wagmi';
import { getAccount, watchAccount, getContract } from '@wagmi/core';
import AOS from 'aos';

// 3RD-PARTY STYLES
import 'react-toastify/dist/ReactToastify.css';
import 'react-h5-audio-player/lib/styles.css';

// HOOKS
import useVideos from '../hooks/useVideos';
import useWeb3 from '../hooks/useWeb3';

// CONTRACT ABIs
import VideosAbi from '../contracts/Collectible.json';

// COMPONENTS
import Header from '../components/general/Header';
import Footer from '../components/general/Footer';
import ScrollToTop from '../components/general/ScrollToTop';
import MetaMaskLoader from '../components/general/MetaMaskLoader';
import PreviewModal from '../components/general/PreviewModal';
import ViewOnlyAlert from '../components/general/ViewOnlyAlert';
import BuyVideoModal from '../components/general/BuyVideoModal';

// CHANGE [bscTestnet] with the chain name you'll deploy on, visit https://wagmi.sh/core/chains#supported-chains to select it from the list
import { celoAlfajores } from 'wagmi/chains';
const chains = [celoAlfajores];
const projectId = appSettings.wcProjectId;

// CONFIGURE WAGMI CLIENT
const { provider, webSocketProvider } = configureChains(chains, [w3mProvider({ projectId })]);
const wagmiClient = createClient({
    autoConnect: true,
    connectors: w3mConnectors({ projectId, version: 1, chains }),
    provider,
    webSocketProvider,
});
const ethereumClient = new EthereumClient(wagmiClient, chains);

function Layout() {
    const { chain } = useNetwork();
    const { account, loadAccount, setUsername } = useWeb3();
    const {
        loadContract,
        loadAllChannels,
        loadAppOwner,
        loadActivities,
        transactionLoading,
        previewModal,
        buyModal,
        loadAppProfits,
        loadUserWatchlist,
        contract,
        loadUserRefCode,
    } = useVideos();

    /*** ------------------------------------------------ */
    //      GENERATE USERNAME FOR CONNECTED ACCOUNT
    /*** ------------------------------------------------ */
    useEffect(() => {
        if (!localStorage.getItem('moviex_username')) {
            setUsername(
                uniqueNamesGenerator({
                    dictionaries: [starWars],
                    seed: account,
                }).replace('_', ' ')
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*** ------------------------------------------------ */
    //      LOAD INITIAL APP DATA
    /*** ------------------------------------------------ */
    useEffect(() => {
        if (contract && account) {
            loadAppProfits(contract, account);
            loadUserWatchlist(contract, account);
            loadAllChannels(contract);
            loadUserRefCode(contract, account);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contract, account]);

    /* -------------------------------------------------- */
    //      AOS ANIMATION
    /* -------------------------------------------------- */
    useEffect(() => {
        AOS.init({
            duration: 700,
            disable: 'mobile',
            once: true,
        });
    }, []);

    /*** -------------------------------------------- */
    //      GET BLOCKCHAIN DATA
    /*** -------------------------------------------- */
    useEffect(() => {
        const calclateInitialSettings = async () => {
            const acc = getAccount();
            loadAccount(acc?.address);
            const videoDeployedNetwork = VideosAbi.networks[appSettings?.networkId];
            const videosContract =
                videoDeployedNetwork &&
                getContract({
                    address: videoDeployedNetwork.address,
                    abi: VideosAbi.abi,
                });

            loadContract(videosContract);
            if (videosContract) {
                loadAllChannels(videosContract);
                loadAppOwner(videosContract);
                loadActivities(videosContract);
            } else {
                return;
            }

            // eslint-disable-next-line no-unused-vars
            const unwatch = watchAccount((account) => {
                loadAccount(account?.address);
                setUsername(
                    uniqueNamesGenerator({
                        dictionaries: [starWars],
                    }).replace('_', ' ')
                );
            });
        };
        calclateInitialSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <WagmiConfig client={wagmiClient}>
                <div className='app'>
                    {chain?.id !== appSettings.networkId && <ViewOnlyAlert />}
                    <Header />
                    <div id='main' className='layout-horizontal z-index-20 position-relative'>
                        <div className='content-wrapper'>
                            <ScrollToTop />
                            <Outlet />
                        </div>
                    </div>
                    <Footer />
                </div>
                <ToastContainer position='top-center' autoClose={1500} />
                {previewModal && <PreviewModal />}
                {buyModal && <BuyVideoModal />}
                {transactionLoading && <MetaMaskLoader />}
            </WagmiConfig>
            <Web3Modal
                projectId={projectId}
                ethereumClient={ethereumClient}
                themeVariables={{
                    '--w3m-z-index': '9999',
                    '--w3m-container-background-color': 'rgba(0,0,0,.7)',
                }}
            />
        </>
    );
}

export default Layout;
